import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, Spin, Tooltip, message } from "antd";
import { SwapOutlined, SearchOutlined } from "@ant-design/icons";
import { getProjects } from "api/projects/getProjects";
import { moveIssueToProject } from "api/issue/issueApi";
import { issueLabel } from "helpers/artifactPrefix";

// "Move to project…" - beside "Duplicate of…", owner side only (owner,
// 2026-09-22: "like merge issues, add move issue to project, so the issue
// updates the project associated with and the prefix and the numbering").
// The issue becomes the other project's: its project, its prefix and the
// next number there (PUT /issue/:id/move). People, comments, files and
// images travel with it; its schedule activity and area belong to the old
// project and are cleared, which the history records.
const T = { muted: "#6B7280", line: "#E5E7EB", bg: "#F9FAFB" };

export function IssueMoveToProjectButton({ issue, canMove, onMoved, style }) {
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState(null);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  const currentId = String(issue?.projectId?._id || issue?.projectId || "");

  useEffect(() => {
    if (!open || projects) return;
    let alive = true;
    getProjects()
      .then((r) => { if (alive) setProjects(((r && r.projects) || (Array.isArray(r) ? r : [])).filter((p) => p && !p.archived)); })
      .catch(() => { if (alive) setProjects([]); });
    return () => { alive = false; };
  }, [open, projects]);

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (projects || [])
      .filter((p) => String(p._id) !== currentId)
      .filter((p) => !term || [p.title, p.projectNumber, p.artifactPrefix].filter(Boolean).join(" ").toLowerCase().includes(term))
      .sort((a, b) => String(a.title || "").localeCompare(String(b.title || "")));
  }, [projects, q, currentId]);

  if (!canMove || !issue || !issue._id || issue.mergedInto) return null;
  const here = issueLabel(issue) || `#${issue.displayId}`;

  const confirm = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      const r = await moveIssueToProject(issue._id, picked._id);
      const label = r?.issue?.label || "";
      message.success(`${here} is now ${label} on "${picked.title}"${r?.cleared?.length ? " - its schedule links on the old project were cleared (see the history)" : ""}`);
      setOpen(false); setPicked(null); setQ("");
      if (onMoved) onMoved(r);
    } catch (e) {
      message.error(e?.err || e?.message || "Move failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Tooltip title="This issue belongs to another project: move it there. It takes that project's prefix and next number; people, comments, files and images go with it.">
        <Button size="small" icon={<SwapOutlined />} onClick={() => { setPicked(null); setQ(""); setOpen(true); }} style={style}>
          Move to project…
        </Button>
      </Tooltip>
      <Modal
        open={open}
        title={`Move ${here} to another project`}
        onCancel={() => { if (!busy) setOpen(false); }}
        okText={picked ? `Move to ${picked.title}` : "Pick a project first"}
        okButtonProps={{ disabled: !picked, loading: busy }}
        onOk={confirm}
        destroyOnClose
        width={560}
      >
        <Input
          autoFocus allowClear
          prefix={<SearchOutlined style={{ color: T.muted }} />}
          placeholder="Find a project by name, number or prefix"
          value={q}
          onChange={(e) => { setQ(e.target.value); setPicked(null); }}
        />
        <div style={{ marginTop: 8, maxHeight: 320, overflow: "auto", border: `1px solid ${T.line}`, borderRadius: 6 }}>
          {!projects ? (
            <div style={{ padding: 24, textAlign: "center" }}><Spin /></div>
          ) : !rows.length ? (
            <div style={{ padding: 16, fontSize: 12.5, color: T.muted }}>{q.trim() ? "No project matches that." : "There is no other project to move it to."}</div>
          ) : rows.map((p) => (
            <div key={p._id} onClick={() => setPicked(p)}
                 style={{ padding: "8px 12px", cursor: "pointer", borderBottom: `1px solid ${T.line}`,
                          background: picked && String(picked._id) === String(p._id) ? "#EEF4FF" : "#fff",
                          display: "flex", justifyContent: "space-between", gap: 8 }}>
              <span style={{ fontWeight: 600 }}>{p.title}</span>
              <span style={{ color: T.muted, fontSize: 12 }}>{[p.artifactPrefix, p.projectNumber].filter(Boolean).join(" · ")}</span>
            </div>
          ))}
        </div>
        {picked && (
          <div style={{ marginTop: 10, fontSize: 12.5, background: T.bg, border: `1px solid ${T.line}`, borderRadius: 6, padding: "8px 10px" }}>
            <b>{here}</b> becomes an issue of <b>{picked.title}</b> with that project's prefix
            {picked.artifactPrefix ? <> (<b>{picked.artifactPrefix}</b>)</> : null} and its next number.
            People, comments, attachments and images go with it. Its schedule activity and area belong
            to this project, so they are cleared; the issue's history keeps the old number and what was cleared.
          </div>
        )}
      </Modal>
    </>
  );
}

export default IssueMoveToProjectButton;
